import React, { useEffect, useState, useMemo } from "react";
import "../styles/index.less";
import { Card, Col, Row, Button, Tag, Typography, Divider } from "antd";
import {
  DoubleRightOutlined,
  RightCircleFilled,
  TeamOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { get_task_active } from "../../../services/task";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../utils/Auth/AuthWrapper";
import dayjs from "dayjs";
import "dayjs/locale/th";
import buddhistEra from "dayjs/plugin/buddhistEra";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(buddhistEra);
dayjs.extend(advancedFormat);
dayjs.locale("th");

const { Title, Text } = Typography;

type Props = {};

// ย้าย helper functions ออกมานอก component
const calculateDaysLeft = (end_date: string) => {
  const end = dayjs(end_date, "YYYY-MM-DD HH:mm:ss");
  const now = dayjs();
  return Math.ceil(end.diff(now, "hour") / 24);
};

const formatThaiDate = (dateString: string) => {
  const date = dayjs(dateString);
  return date.format("D MMM BBBB");
};

// ฟังก์ชันดึงวันที่จาก event_date
const getEventDates = (event: any) => {
  let startDate = null;
  let endDate = null;

  try {
    if (event.event_date) {
      let eventData = event.event_date;

      // Parse JSON ซ้อนกัน
      while (typeof eventData === 'string') {
        try {
          eventData = JSON.parse(eventData);
        } catch (e) {
          break;
        }
      }

      // ถ้าเป็น array ให้เอา element แรก
      if (Array.isArray(eventData) && eventData.length > 0) {
        eventData = eventData[0];
      }

      // ดึงค่า start_date และ end_date
      if (eventData && typeof eventData === 'object') {
        startDate = eventData.start_date;
        endDate = eventData.end_date;
      }
    }

    // Fallback ไปใช้ฟิลด์ start_date และ end_date แบบเดิม
    if (!startDate && event.start_date) {
      startDate = event.start_date;
    }
    if (!endDate && event.end_date) {
      endDate = event.end_date;
    }
  } catch (error) {
    console.error('Error parsing event_date:', error, event);
  }

  return { startDate, endDate };
};

const Contask = (props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = user?.user_id !== null && user?.user_id !== undefined;
  const [visibleCount, setVisibleCount] = useState(3);
  const [expandedTags, setExpandedTags] = useState<{ [key: string]: boolean }>({});
  const [taskDataHome, settaskDataHome] = useState<
    {
      key: string;
      id: string;
      task_id: string;
      type: string;
      name: string;
      description: string;
      created_date: string;
      img: string;
      start_date: string;
      end_date: string;
      created_by: string;
      details: string;
      tags: string | string[];
    }[]
  >([]);

  // api task for home
  useEffect(() => {
    getTaskHome();
  }, []);

  const getTaskHome = async () => {
    try {
      const res = await get_task_active({
        fields: ["*"],
        filter: {
          status: 1,
        },
      });
      settaskDataHome(res.result);
    } catch (e) {
      console.error(e);
    }
  };

  // useMemo เพื่อ memoize filtered data
  const visibleTasks = useMemo(() => {
    return taskDataHome.slice(0, visibleCount);
  }, [taskDataHome, visibleCount]);


  return (
    <div className="globalcontainer">
      {/* head */}
      <div className="task-section">
        <div className="section-title" data-aos="fade-up">
          <Divider
            className="dividline"
            style={{ backgroundColor: "#363636" }}
          />
        </div>
      </div>

      {/* Left Section: Task Title */}
      <div style={{ padding: "10px 0px 10px 0px" }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={24} md={24} lg={6}>
            <div className="task-header">
              <span className="red-bar"></span>
              <p
                className="detail-title"
                style={{
                  textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                  marginBottom: "0px",
                  fontFamily: "IBM Plex Sans Thai",
                  color: "#052240",
                }}
              >
                {t("Task")}
              </p>
            </div>
            <div className="detailtask-group">
              <p
                className="detailtask"
                style={{ color: "#363636", fontFamily: "IBM Plex Sans Thai" }}
              >
                This is all the tasks for you.
              </p>
              <p
                className="detailtask"
                style={{ color: "#6C757D", fontFamily: "IBM Plex Sans Thai" }}
              >
                Here's all the tasks for you. You can view all tasks.
              </p>
              <Button
                type="primary"
                size="large"
                className="btnanime"
                style={{
                  marginTop: "20px",
                  backgroundColor: "#052240",
                  borderColor: "#052240",
                  fontSize: "20px",
                  width: "200px",
                  borderRadius: "20px",
                }}
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate("/task");
                }}
              >
                {t("All Tasks")}{" "}
                <RightCircleFilled style={{ color: "#BC224A" }} />
              </Button>
            </div>
          </Col>

          {/* Right Section: Task cards */}
          <Col xs={24} sm={24} md={24} lg={24} xl={18} xxl={18}>
            <Row gutter={[16, 16]}>
              {visibleTasks.map((event: any) => {
                const tags = JSON.parse(event.tags || "[]");

                // ดึงวันที่จาก event_date
                const { startDate, endDate } = getEventDates(event);

                const daysLeft = endDate ? calculateDaysLeft(endDate) : 0;
                let end_dateStyle = {};
                let end_dateDisplay = endDate ? formatThaiDate(endDate) : "-";
                let end_dateClass = "";

                if (endDate) {
                  if (daysLeft <= 0) {
                    end_dateStyle = {
                      color: "red",
                      fontSize: "16px",
                      fontFamily: "IBM Plex Sans Thai",
                    };
                    end_dateDisplay = t("Registration ended");
                  } else if (daysLeft < 5) {
                    end_dateStyle = { fontSize: "18px" };
                    end_dateDisplay = `${daysLeft} ${t("Day")}`;
                    end_dateClass = "blink";
                  }
                }

                return (
                  <Col
                    xs={24}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={8}
                    xxl={8}
                    key={event.id}
                  >
                    <Card
                      cover={
                        <div
                          style={{
                            position: "relative",
                            paddingTop: "56.25%",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            alt={event.name}
                            src={
                              event.img?.startsWith("/data/")
                                ? `${process.env.REACT_APP_API}${event.img}`
                                : event.img
                            }
                            style={{
                              position: "absolute",
                              padding: "5px",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "30px",
                            }}
                          />
                        </div>
                      }
                      hoverable
                      style={{
                        borderRadius: "25px",
                        overflow: "hidden",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                      className="custom-card"
                      bodyStyle={{ padding: "0", flex: 1, display: "flex", flexDirection: "column" }}
                    >
                      <div style={{ padding: "10px 10px 10px 10px", display: "flex", flexDirection: "column", height: "100%" }}>
                        <Row>
                          <b
                            className="event-name"
                            style={{
                              fontSize: 18,
                              width: "100%",
                              marginBottom: 10,
                            }}
                          >
                            {event.name}
                          </b>
                        </Row>

                        {/* tag */}
                        <Row align="top" style={{ marginBottom: 8 }}>
                          <Col span={24}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: '32px' }}>
                              <p className="startend" style={{ marginBottom: 0, marginRight: 10, flexShrink: 0 }}>
                                {t("Tag")} :
                              </p>
                              {tags.length > 0 ? (
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                  flex: 1
                                }}>
                                  {expandedTags[event.task_id] ? (
                                    // แสดง tag ทั้งหมดเมื่อขยาย
                                    <div style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: '4px'
                                    }}>
                                      {tags.map((tag: any, index: number) => (
                                        <Tag
                                          key={`${event.task_id}-tag-${index}`}
                                          className="tag-item"
                                          style={{
                                            margin: 0,
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {tag}
                                        </Tag>
                                      ))}
                                      <Tag
                                        className="tag-item"
                                        style={{
                                          margin: 0,
                                          cursor: 'pointer',
                                          whiteSpace: 'nowrap',
                                          backgroundColor: '#f0f0f0',
                                          borderStyle: 'dashed'
                                        }}
                                        onClick={() => {
                                          setExpandedTags(prev => ({
                                            ...prev,
                                            [event.task_id]: false
                                          }));
                                        }}
                                      >
                                        {t("Show less") || "แสดงน้อยลง"}
                                      </Tag>
                                    </div>
                                  ) : (
                                    // แสดง tag เดียวและปุ่ม +
                                    <div style={{
                                      display: 'flex',
                                      flexWrap: 'nowrap',
                                      gap: '4px'
                                    }}>
                                      <Tag
                                        className="tag-item"
                                        style={{
                                          margin: 0,
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {tags[0]}
                                      </Tag>
                                      {tags.length > 1 && (
                                        <Tag
                                          className="tag-item"
                                          style={{
                                            margin: 0,
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap'
                                          }}
                                          onClick={() => {
                                            setExpandedTags(prev => ({
                                              ...prev,
                                              [event.task_id]: true
                                            }));
                                          }}
                                        >
                                          +{tags.length - 1}
                                        </Tag>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="start-date"> - </span>
                              )}
                            </div>
                          </Col>
                        </Row>

                        {/* date */}
                        <div style={{ flex: 1 }}>
                          <Row className="event-details">
                            <Col xs={24}>
                              <Row justify={"space-between"}>
                                <Col xs={11} sm={9} md={7} lg={5} xl={9}>
                                  <p className="startend">{t("Start date")} : </p>
                                </Col>
                                <Col xs={12} sm={14} md={16} lg={18} xl={14}>
                                  <span className="start-date">
                                    {/* {event.type === 2
                                      ? " - "
                                      : startDate
                                      ? formatThaiDate(startDate)
                                      : " - "} */}
                                    {startDate ? formatThaiDate(startDate) : " - "}
                                  </span>
                                </Col>
                              </Row>
                            </Col>
                            <Col xs={24} style={{ marginBottom: 10 }}>
                              <Row justify={"space-between"}>
                                <Col xs={11} sm={9} md={7} lg={5} xl={9}>
                                  <p className="startend">{t("End date")} : </p>
                                </Col>
                                <Col xs={12} sm={14} md={16} lg={18} xl={14}>
                                  <span
                                    className={`end-date ${end_dateClass}`}
                                    style={end_dateStyle}
                                  >
                                    {/* {event.type === 2
                                      ? " - "
                                      : endDate
                                      ? end_dateDisplay
                                      : " - "} */}
                                    {endDate ? end_dateDisplay : " - "}
                                  </span>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </div>

                        {/* footer */}
                        <Row justify={"space-between"} align={"middle"} style={{ marginTop: "auto" }}>
                          <Divider
                            style={{
                              marginTop: "1px",
                              marginBottom: "15px",
                              borderColor: "#7A7A7A",
                            }}
                          />
                          <Col>
                            <Button
                              className="btnread"
                              type="link"
                              onClick={() => {
                                window.scrollTo(0, 0);
                                navigate(`/task/detail/${event.task_id}`, {
                                  state: { EventName: event.task_id },
                                });
                              }}
                            >
                              {t("Read More")}
                              <DoubleRightOutlined className="iconright" />
                            </Button>
                          </Col>
                          <Col>
                            {/* {isLoggedIn && (
                              <Button
                                className="btnlead"
                                style={{ margin: "5px" }}
                                type="primary"
                                shape="circle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.scrollTo(0, 0);
                                  navigate(`/team/${event.task_id}`);
                                }}
                              >
                                <TeamOutlined style={{ color: "#ffffff" }} />
                              </Button>
                            )} */}
                            <Button
                              className="btnlead"
                              type="primary"
                              shape="circle"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.scrollTo(0, 0);
                                navigate(`/score/leaderboard/${event.task_id}`);
                              }}
                            >
                              <TrophyOutlined style={{ color: "#ffffff" }} />
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Contask;